import React, {useEffect, useState} from 'react'
import {Menu} from 'antd'
import {
    AppstoreOutlined,
    BankOutlined,
    BarChartOutlined,
    BranchesOutlined,
    BuildOutlined,
    CarOutlined,
    ContainerOutlined,
    CreditCardOutlined,
    CrownOutlined,
    DesktopOutlined,
    FileAddOutlined,
    FileDoneOutlined,
    FileProtectOutlined,
    FileSyncOutlined,
    FireOutlined,
    GlobalOutlined,
    HistoryOutlined,
    LogoutOutlined,
    PictureOutlined,
    ProfileOutlined,
    SecurityScanOutlined,
    TeamOutlined,
    UserOutlined,
} from '@ant-design/icons'
import {NavLink, useLocation} from 'react-router-dom'
import {useAppSelector} from '../../hooks'
import {signOut} from './Panel'
import {UserRole} from "../../api/userApi";

const Nav = () => {
    const {role} = useAppSelector(state => state.user)

    const [current, setCurrent] = useState<RolePermissionsKeyType | string>('')
    const location = useLocation()

    useEffect(() => {
        const pathName = location.pathname.slice(1) as RolePermissionsKeyType;
        setCurrent(rolePermissionsKey[role].includes(pathName) ? pathName : '')
    }, [location])

    const businessChildren: NavItemType[] = [
        {label: <NavLink to="business/industry">Industry</NavLink>, key: 'business/industry', icon: <BuildOutlined/>},
        {label: <NavLink to="business/brand">Brand</NavLink>, key: 'business/brand', icon: <FireOutlined/>},
        {label: <NavLink to="business/domains">Domains</NavLink>, key: 'business/domains', icon: <BranchesOutlined/>},
        {label: <NavLink to="business/company">Company</NavLink>, key: 'business/company', icon: <GlobalOutlined/>},
        {
            label: <NavLink to="business/style-guide">Style Guide</NavLink>,
            key: 'business/style-guide',
            icon: <PictureOutlined/>
        }
    ]

    const usersChildren: NavItemType[] = [
        {label: <NavLink to="users/dealers">Dealers</NavLink>, key: 'users/dealers', icon: <UserOutlined/>},
        {label: <NavLink to="users/admins">Admins</NavLink>, key: 'users/admins', icon: <UserOutlined/>},
        {
            label: <NavLink to="users/account-managers">Account Managers</NavLink>,
            key: 'users/account-managers',
            icon: <UserOutlined/>
        },
        {
            label: <NavLink to="users/sales-managers">Sales Managers</NavLink>,
            key: 'users/sales-managers',
            icon: <UserOutlined/>
        }
    ]

    const profileChildren: NavItemType[] = [
        {label: <NavLink to="account">Account</NavLink>, key: 'account', icon: <CrownOutlined/>},
        {label: <NavLink to="dealer/account">Account</NavLink>, key: 'dealer/account', icon: <CrownOutlined/>},
        {label: <NavLink to="account/tfa">2FA</NavLink>, key: 'account/tfa', icon: <SecurityScanOutlined/>}
    ]

    const licensesChildren: NavItemType[] = [
        {label: <NavLink to="licenses">Management</NavLink>, key: 'licenses', icon: <FileSyncOutlined/>},
        {
            label: <NavLink to="licenses/activated">Activated</NavLink>,
            key: 'licenses/activated',
            icon: <FileDoneOutlined/>
        },
        {
            label: <NavLink to="licenses/available">Available</NavLink>,
            key: 'licenses/available',
            icon: <FileAddOutlined/>
        },
        {label: <NavLink to="licenses/billing">Billing</NavLink>, key: 'licenses/billing', icon: <HistoryOutlined/>},
    ]

    const dealerItems: NavItemType[] = [
        {label: <NavLink to="dealer/invoices">Invoices</NavLink>, key: 'dealer/invoices', icon: <BankOutlined/>},
        {label: <NavLink to="dealer/analytics">Analytics</NavLink>, key: 'dealer/analytics', icon: <BarChartOutlined/>},
        {label: <NavLink to="dealer/payment">Payment</NavLink>, key: 'dealer/payment', icon: <CreditCardOutlined/>},
        {label: <NavLink to="dealer/catalog">Catalog</NavLink>, key: 'dealer/catalog', icon: <CarOutlined/>},
    ]

    const adminItems: NavItemType[] = [
        {label: <NavLink to="company/catalog">Catalog</NavLink>, key: 'company/catalog', icon: <CarOutlined/>},
        {label: <NavLink to="company/invoices">Invoices</NavLink>, key: 'company/invoices', icon: <BankOutlined/>},
        {
            label: <NavLink to="company/analytics">Analytics</NavLink>,
            key: 'company/analytics',
            icon: <BarChartOutlined/>
        },
        {label: <NavLink to="company/payment">Payment</NavLink>, key: 'company/payment', icon: <CreditCardOutlined/>},
    ]

    const items: NavItemsType = [
        {label: <NavLink to="assets">Assets</NavLink>, key: 'assets', icon: <AppstoreOutlined/>},

        {label: 'Licenses', key: 'licenses-dropdown', icon: <FileProtectOutlined/>, children: licensesChildren},
        {label: 'Business', key: 'business-dropdown', icon: <DesktopOutlined/>, children: businessChildren},
        {label: 'Users', key: 'users-dropdown', icon: <TeamOutlined/>, children: usersChildren},
        {label: 'Profile', key: 'profile-dropdown', icon: <ProfileOutlined/>, children: profileChildren},

        ...dealerItems,
        ...adminItems,

        {label: <div onClick={signOut}>Logout</div>, key: 'logout', icon: <LogoutOutlined/>}
    ]

    const filteredItems = items.filter(item => {
        if ('children' in item) {
            item.children = item.children.filter(child => (
                rolePermissionsKey[role].includes(child.key)
            ))

            return item.children.length !== 0 && item
        }

        return rolePermissionsKey[role].includes(item.key) || item.key === 'logout'
    })

    return <Menu theme="dark" mode="inline" selectedKeys={[current]} items={filteredItems}/>
}

export default Nav

export const rolePermissionsKey: { [key in UserRole]: RolePermissionsKeyType[] } = {
    'super admin': [
        'business/company',
        'business/industry',
        'business/brand',
        'business/style-guide',

        'users/admins',
        'users/account-managers',

        'account/tfa',
        "account",
    ],
    admin: [
        'licenses',

        'assets',

        'users/dealers',

        'company/invoices',
        'company/analytics',
        'company/payment',
        'company/catalog',

        'account/tfa',
        "account",
    ],
    'account manager': [
        'licenses',

        'users/dealers',
        'business/company',

        'account/tfa',
        "account",
    ],
    dealer: [
        'licenses/activated',
        'licenses/available',
        'licenses/billing',

        'users/sales-managers',
        'business/domains',

        'dealer/invoices',
        'dealer/analytics',
        'dealer/payment',
        'dealer/catalog',
        'dealer/account',

        'account/tfa'
    ],
    'sale member': [
        'account',
        'account/tfa',
    ]
}

type NavItemType = { label: JSX.Element, key: RolePermissionsKeyType, icon: JSX.Element }
type NavDropdownItemType = { label: string, key: string, icon: JSX.Element, children: NavItemType[] }

type NavItemsType = (NavItemType | NavDropdownItemType)[]

type RolePermissionsKeyType =
    | 'assets'

    | 'business/industry'
    | 'business/brand'
    | 'business/style-guide'
    | 'business/company'
    | 'business/domains'

    | 'users/dealers'
    | 'users/admins'
    | 'users/account-managers'
    | 'users/sales-managers'

    | 'account'
    | 'account/tfa'

    | 'dealer/account'
    | 'dealer/invoices'
    | 'dealer/analytics'
    | 'dealer/payment'
    | 'dealer/catalog'

    | 'company/invoices'
    | 'company/analytics'
    | 'company/payment'
    | 'company/catalog'

    | 'licenses'
    | 'licenses/activated'
    | 'licenses/available'
    | 'licenses/billing'

    | 'logout'

